/**
 * Dashboard Service
 * 
 * This service handles dashboard statistics, analytics, notifications,
 * and real-time data for the healthcare management system.
 */

import apiClient from './apiClient';

class DashboardService {
    constructor() {
        this.baseURL = '/dashboard';
    }

    // ============================================================================
    // DASHBOARD STATISTICS
    // ============================================================================

    /**
     * Get dashboard statistics for current user
     */
    async getDashboardStatistics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/statistics`, { params });
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Dashboard statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch dashboard statistics",
                data: {}
            };
        }
    }

    /**
     * Get legacy dashboard statistics (for backward compatibility)
     */
    async getLegacyDashboardStats(requestData = {}) {
        try {
            const response = await apiClient.post('/legacy/getDashboardStats', requestData);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Legacy dashboard statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch legacy dashboard statistics",
                data: {}
            };
        }
    }

    /**
     * Get system statistics
     */
    async getSystemStats(requestData = {}) {
        try {
            const response = await apiClient.post('/legacy/getSystemStats', requestData);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "System statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch system statistics",
                data: {}
            };
        }
    }

    // ============================================================================
    // NOTIFICATIONS
    // ============================================================================

    /**
     * Get user notifications
     */
    async getNotifications(params = {}) {
        try {
            // Use the correct notifications endpoint that matches the backend
            const response = await apiClient.get('/notifications', { params });
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Notifications fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch notifications",
                data: []
            };
        }
    }

    /**
     * Mark notification as read
     */
    async markNotificationAsRead(notificationId) {
        try {
            const response = await apiClient.patch(`${this.baseURL}/notifications/${notificationId}/read`);
            return {
                success: true,
                data: response.data,
                message: "Notification marked as read"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to mark notification as read"
            };
        }
    }

    /**
     * Mark all notifications as read
     */
    async markAllNotificationsAsRead() {
        try {
            const response = await apiClient.patch(`${this.baseURL}/notifications/mark-all-read`);
            return {
                success: true,
                data: response.data,
                message: "All notifications marked as read"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to mark all notifications as read"
            };
        }
    }

    // ============================================================================
    // FORM MENU AND NAVIGATION
    // ============================================================================

    /**
     * Get form menu for user based on permissions
     */
    async getFormMenu(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/form-menu`, { params });
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Form menu fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form menu",
                data: {}
            };
        }
    }

    /**
     * Get form data list for dashboard
     */
    async getFormDataList(requestData = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/form-data-list`, requestData);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Form data list fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form data list",
                data: []
            };
        }
    }

    // ============================================================================
    // ANALYTICS AND REPORTS
    // ============================================================================

    /**
     * Get patient analytics
     */
    async getPatientAnalytics(params = {}) {
        try {
            const response = await apiClient.get(`/patients/statistics`, { params });
            return {
                success: true,
                data: response.data,
                message: "Patient analytics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient analytics",
                data: {}
            };
        }
    }

    /**
     * Get form submission analytics
     */
    async getFormAnalytics(params = {}) {
        try {
            const response = await apiClient.get(`/dynamic-forms/analytics/submission-stats`, { params });
            return {
                success: true,
                data: response.data,
                message: "Form analytics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form analytics",
                data: {}
            };
        }
    }

    /**
     * Get medication analytics
     */
    async getMedicationAnalytics(params = {}) {
        try {
            const response = await apiClient.get(`/medications/analytics/metrics`, { params });
            return {
                success: true,
                data: response.data,
                message: "Medication analytics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch medication analytics",
                data: {}
            };
        }
    }

    /**
     * Get scheduling analytics
     */
    async getSchedulingAnalytics(params = {}) {
        try {
            const response = await apiClient.get(`/scheduling/analytics/dashboard`, { params });
            return {
                success: true,
                data: response.data,
                message: "Scheduling analytics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch scheduling analytics",
                data: {}
            };
        }
    }

    // ============================================================================
    // REAL-TIME DATA
    // ============================================================================

    /**
     * Get recent activities
     */
    async getRecentActivities(params = {}) {
        try {
            // Combine multiple sources for recent activities
            const [formSubmissions, patientUpdates, appointments] = await Promise.allSettled([
                apiClient.get('/dynamic-forms/submissions', { 
                    params: { ...params, limit: 10, sort_by: 'submitted_at', sort_order: 'desc' } 
                }),
                apiClient.get('/patients/', { 
                    params: { ...params, limit: 10, sort_by: 'updated_at', sort_order: 'desc' } 
                }),
                apiClient.get('/scheduling/appointments', { 
                    params: { ...params, limit: 10, sort_by: 'created_at', sort_order: 'desc' } 
                })
            ]);

            const activities = [];
            
            if (formSubmissions.status === 'fulfilled') {
                const forms = formSubmissions.value.data.items || [];
                activities.push(...forms.map(form => ({
                    type: 'form_submission',
                    title: 'Form Submitted',
                    description: `Form "${form.form_name}" submitted`,
                    timestamp: form.submitted_at,
                    id: form.submission_id
                })));
            }

            if (patientUpdates.status === 'fulfilled') {
                const patients = patientUpdates.value.data.users || [];
                activities.push(...patients.map(patient => ({
                    type: 'patient_update',
                    title: 'Patient Updated',
                    description: `Patient "${patient.full_name}" updated`,
                    timestamp: patient.updated_at,
                    id: patient.id
                })));
            }

            // Sort by timestamp
            activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            return {
                success: true,
                data: activities.slice(0, 20), // Return top 20 activities
                message: "Recent activities fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch recent activities",
                data: []
            };
        }
    }

    /**
     * Get upcoming events
     */
    async getUpcomingEvents(params = {}) {
        try {
            const response = await apiClient.get('/scheduling/appointments', {
                params: {
                    ...params,
                    start_date: new Date().toISOString().split('T')[0],
                    status: 'scheduled',
                    limit: 20,
                    sort_by: 'appointment_date',
                    sort_order: 'asc'
                }
            });

            return {
                success: true,
                data: response.data || [],
                message: "Upcoming events fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch upcoming events",
                data: []
            };
        }
    }

    // ============================================================================
    // PATIENT RECORD DASHBOARD
    // ============================================================================

    /**
     * Get patient record dashboard statistics
     */
    async getPatientRecordDashboard(params = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/patient-record-dashboard`, params);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Patient record dashboard data fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient record dashboard data",
                data: {
                    totalPatient: 0,
                    dischargePatient: 0,
                    activePatient: 0
                }
            };
        }
    }

    /**
     * Get medication prescription list for a patient
     */
    async getMedpassPrescriptionList(params = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/medpass-prescription-list`, params);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Medication prescription list fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch medication prescription list",
                data: []
            };
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Get dashboard configuration for user role
     */
    getDashboardConfig(userRole) {
        const configs = {
            admin: {
                widgets: ['statistics', 'recent_activities', 'system_health', 'user_analytics'],
                refreshInterval: 30000 // 30 seconds
            },
            doctor: {
                widgets: ['patient_statistics', 'appointments', 'prescriptions', 'recent_activities'],
                refreshInterval: 60000 // 1 minute
            },
            nurse: {
                widgets: ['patient_care', 'schedules', 'medications', 'forms'],
                refreshInterval: 60000 // 1 minute
            },
            staff: {
                widgets: ['assignments', 'forms', 'schedules'],
                refreshInterval: 120000 // 2 minutes
            }
        };

        return configs[userRole] || configs.staff;
    }

    /**
     * Format statistics for display
     */
    formatStatistics(stats) {
        return {
            patients: {
                total: stats.total_patients || 0,
                active: stats.active_patients || 0,
                new_today: stats.new_patients_today || 0,
                new_week: stats.new_patients_this_week || 0,
                new_month: stats.new_patients_this_month || 0
            },
            appointments: {
                today: stats.appointments_today || 0,
                week: stats.appointments_this_week || 0,
                pending: stats.pending_appointments || 0,
                completed: stats.completed_appointments || 0
            },
            forms: {
                total: stats.total_forms || 0,
                submitted_today: stats.forms_submitted_today || 0,
                pending: stats.pending_forms || 0
            },
            staff: {
                total: stats.total_staff || 0,
                active: stats.active_staff || 0,
                on_duty: stats.staff_on_duty || 0
            }
        };
    }
}

export default new DashboardService(); 